import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type AppRole = 'admin' | 'manager' | 'sales_director' | 'sales_manager' | 'finance_analyst' | 'finance_manager' | 'user';

interface UserRoleData {
  role: AppRole | null;
  loading: boolean;
  error: Error | null;
}

export function useUserRole(): UserRoleData & { hasRole: (role: AppRole) => boolean } {
  const { user } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setLoading(false);
      return;
    }

    const fetchUserRole = async () => {
      try {
        setLoading(true);
        
        // Buscar AMBAS as fontes em paralelo
        const [appUserResult, legacyRolesResult] = await Promise.all([
          supabase
            .from('app_users')
            .select('system_role:system_roles(code)')
            .eq('auth_user_id', user.id)
            .single(),
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
        ]);

        // Verificar se é admin em user_roles (tabela legada - permite override)
        const legacyRoles = legacyRolesResult.data?.map(r => (r as any).role) || [];
        const isAdminInLegacy = legacyRoles.includes('admin');
        
        // Se for admin em user_roles, dar prioridade a isso
        if (isAdminInLegacy) {
          setRole('admin');
          return;
        }

        // Caso contrário, usar system_role do app_users
        const systemRoleCode = appUserResult.data?.system_role?.code;
        if (systemRoleCode) {
          if (systemRoleCode === 'admin') setRole('admin');
          else if (systemRoleCode === 'sales_director') setRole('sales_director');
          else if (systemRoleCode === 'sales_manager') setRole('sales_manager');
          else if (systemRoleCode === 'manager') setRole('manager');
          else if (systemRoleCode === 'finance_analyst') setRole('finance_analyst');
          else if (systemRoleCode === 'finance_manager') setRole('finance_manager');
          else setRole('user');
          return;
        }

        // Fallback final: verificar outros roles na tabela legada
        if (legacyRoles.includes('manager')) setRole('manager');
        else setRole('user');
        
      } catch (err) {
        console.error('Error fetching user role:', err);
        setError(err as Error);
        setRole('user');
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('user_roles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new && 'role' in payload.new) {
            setRole(payload.new.role as AppRole);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const hasRole = (requiredRole: AppRole): boolean => {
    if (!role) return false;
    
    // Admin has access to everything
    if (role === 'admin') return true;
    
    // Otherwise, exact match required
    return role === requiredRole;
  };

  return { role, loading, error, hasRole };
}
